import Column from '@amzn/meridian/column';
import styled from '@emotion/styled';
import React, { FC, useEffect, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useHrpsApi } from '../../clients/useHrpsApi';
import { WaitFor } from '../../components/blocks';
import { Alert, useTranslation } from '../../components/blocks/localization';
import { usePageMessaging } from '../../components/composites';
import { CreateIssueModal } from '../../components/constructed';
import { DUPLICATE_PURCHASE_ORDER_TYPE } from '../../constants';
import {
    addLine,
    clearPoReviewDraft,
    loadPoReviewDraft,
    removeLine,
    setDeliveryDate,
    setPoNumber,
    setRowItem,
    setRowQuantity,
    setShipDate,
    setShippingLocationCode,
} from '../../redux/actions/poReviewDraftActionCreators';
import {
    getResellerDetails,
    getResellerItems,
    getResellerPriceList,
    getUnmappedItems,
} from '../../redux/actions/resellerActionCreators';
import { PoReviewDraft, PoReviewRow, RootState } from '../../redux/types';
import { CodedError, SiteMapPage } from '../../types';
import { toEpochSeconds } from '../../utils/dateUtils';
import { Logger } from '../../utils/logger';
import { useSiteMapRouter } from '../../utils/SiteMapRouter';
import { useDownloader } from '../../utils/useDownloader';
import { formattedEstimatedSubtotal, hasPriceList, realSku } from './components/poReviewColumns';
import { PoReviewDetailsBox } from './components/PoReviewDetailsBox';
import { PoReviewFooterActions } from './components/PoReviewFooterActions';
import { PoReviewLineItemsTile } from './components/PoReviewLineItemsTile';
import { PoReviewSubmitModal } from './components/PoReviewSubmitModal';
import { buildAddressMap } from './poReviewAddressMap';
import { isDeliveryDateFinding, isPoNumberFinding, isShipDateFinding } from './poReviewFindings';
import { focusControl } from './poReviewFocus';
import { poReviewSubmitState } from './poReviewSubmitState';
import { isPoNumberInError, isStillAlreadyOrdered, SubmitBlocker } from './poReviewValidation';
import { usePoReviewData } from './usePoReviewData';

const TEST_ID = 'purchaseOrderReviewPage';

const PAGE_TRANSLATION_NAMESPACES = [
    'purchaseOrderReviewPage',
    'common',
    'createReturnOrderPage',
    'returnOrderItemList',
    'createNotificationPopover',
    'labeledInput',
    'returnOrder',
];

const DUPLICATE_PURCHASE_ORDER_ERROR_CODE = 'DuplicatePurchaseOrder';

const MainColumn = styled(Column)`
    padding: 0 clamp(5px, 5vw, 200px);
`;

const HEADER_BLOCKER_TEST_IDS = {
    poNumber: `${TEST_ID}.details.poNumber`,
    duplicatePoNumber: `${TEST_ID}.details.poNumber`,
    shipDate: `${TEST_ID}.details.shipDate`,
    deliveryDate: `${TEST_ID}.details.deliveryDate`,
    shippingLocation: `${TEST_ID}.details.shippingLocation`,
    lineItems: `${TEST_ID}.lineItems.addLineButton`,
};

function blockerTestId(blocker: SubmitBlocker): string {
    if (blocker.kind === 'item') {
        return `${TEST_ID}.lineItems.row[${blocker.rowId}].itemSelect`;
    }
    if (blocker.kind === 'quantity') {
        return `${TEST_ID}.lineItems.row[${blocker.rowId}].quantityInput`;
    }
    return HEADER_BLOCKER_TEST_IDS[blocker.kind];
}

// TODO The presigned URL is captured when the draft loads and expires about fifteen minutes later, so a tab
// left open long enough will need a refresh before it resolves.
function buildDownloadSourceDocumentHandler(download: (url: string) => void, draft?: PoReviewDraft): () => void {
    return () => {
        const presignedURL = draft?.sourceDocument?.presignedURL;
        if (presignedURL) download(presignedURL);
    };
}

// eslint-disable-next-line max-lines-per-function
const PurchaseOrderReviewPage: FC<PurchaseOrderReviewPageProps> = ({
    asinToImageUrls,
    resellerId,
    username,
    unmappedItemStatus,
    clearPoReviewDraft,
    getResellerDetails,
    getResellerItems,
    getResellerPriceList,
    getUnmappedItems,
    loadPoReviewDraft,
    addLine,
    removeLine,
    setDeliveryDate,
    setPoNumber,
    setRowItem,
    setRowQuantity,
    setShipDate,
    setShippingLocationCode,
    draft: draftState,
    resellerDetails: resellerDetailsState,
    resellerItems: resellerItemsState,
    resellerPriceList: resellerPriceListState,
}) => {
    const { t } = useTranslation(PAGE_TRANSLATION_NAMESPACES);
    const { cancel, goto } = useSiteMapRouter();
    const { download } = useDownloader();
    const { extractionId } = useParams<{ extractionId: string }>();
    const { showSuccess, showError } = usePageMessaging();
    const {
        extractions: { createSalesOrder },
    } = useHrpsApi();

    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isPageBusy, setIsPageBusy] = useState(false);
    const [isSubmitModalOpen, setIsSubmitModalOpen] = useState(false);
    const [submitAttempted, setSubmitAttempted] = useState(false);
    const [tableResetCount, setTableResetCount] = useState(0);
    const [ticketRequestType, setTicketRequestType] = useState<string | undefined>(undefined);

    const { draft, resellerDetails, resellerItems, resellerPriceList, itemMap } = usePoReviewData({
        resellerId,
        extractionId,
        draft: draftState,
        resellerDetails: resellerDetailsState,
        resellerItems: resellerItemsState,
        resellerPriceList: resellerPriceListState,
        unmappedItemStatus,
        loadPoReviewDraft,
        getResellerDetails,
        getResellerItems,
        getResellerPriceList,
        getUnmappedItems,
        t,
        showError,
    });

    useEffect(
        () => () => {
            clearPoReviewDraft();
        },
        [clearPoReviewDraft]
    );

    useEffect(() => {
        setSubmitAttempted(false);
        setTableResetCount(0);
    }, [extractionId]);

    function asinForItem(itemId?: string): string | undefined {
        return itemId ? itemMap.get(itemId)?.asin : undefined;
    }

    const headerFindings = draft.value?.documentFindings ?? [];
    const poNumberFindings = headerFindings.filter(
        (finding) =>
            isPoNumberFinding(finding) &&
            (finding.type !== DUPLICATE_PURCHASE_ORDER_TYPE || isStillAlreadyOrdered(draft.value))
    );
    const shipDateFindings = headerFindings.filter(isShipDateFinding);
    const deliveryDateFindings = headerFindings.filter(isDeliveryDateFinding);
    const tableFindings = headerFindings.filter(
        (finding) => !isPoNumberFinding(finding) && !isShipDateFinding(finding) && !isDeliveryDateFinding(finding)
    );

    function asinForRow(row: PoReviewRow): string {
        return asinForItem(row.itemId) ?? '';
    }

    // KidsFacade substitutes a placeholder when the reseller has no SKU mapping for the item, so a row
    // without a real SKU sends its ASIN instead.
    function resellerSkuForRow(row: PoReviewRow): string {
        const item = row.itemId ? itemMap.get(row.itemId) : undefined;
        return (item && realSku(item)) ?? asinForRow(row);
    }

    function handleItemChange(rowId: string, itemId?: string): void {
        setRowItem(rowId, itemId);
    }

    function handleAddLine(): void {
        setSubmitAttempted(false);
        addLine();
    }

    const handleDownloadSourceDocument = buildDownloadSourceDocumentHandler(download, draft.value);

    async function submitPurchaseOrder(reviewedDraft: PoReviewDraft): Promise<void> {
        setIsPageBusy(true);
        try {
            await createSalesOrder({
                resellerId: resellerId,
                extractionId: reviewedDraft.extractionId,
                purchaseOrderId: reviewedDraft.poNumber,
                shipToLocationCode: reviewedDraft.shippingLocationCode,
                requestedShipDate: reviewedDraft.shipDate ? toEpochSeconds(reviewedDraft.shipDate) : undefined,
                requestedDeliveryDate: reviewedDraft.deliveryDate
                    ? toEpochSeconds(reviewedDraft.deliveryDate)
                    : undefined,
                lineItems: reviewedDraft.rows.map((row) => ({
                    resellerSku: resellerSkuForRow(row),
                    asin: asinForRow(row),
                    quantity: parseInt(row.quantity, 10),
                })),
            });
            setIsSubmitted(true);
            showSuccess(t('createdPurchaseOrder-successMessage'));
            goto(SiteMapPage.listExtractions);
        } catch (error) {
            Logger.debug(`failed to create purchase order from extraction ${reviewedDraft.extractionId}: ${error}`);
            if ((error as CodedError)?.code === DUPLICATE_PURCHASE_ORDER_ERROR_CODE) {
                showError(t('duplicatePurchaseOrder-errorMessage'));
            } else {
                showError(t('failedToCreatePurchaseOrder-errorMessage'));
            }
        } finally {
            setIsPageBusy(false);
        }
    }

    const pageBody = (
        <WaitFor lazyValue={draft}>
            {(loadedDraft: PoReviewDraft) => (
                <WaitFor lazyValue={resellerItems}>
                    {() => (
                        <WaitFor lazyValue={resellerPriceList}>
                            {() => (
                                <WaitFor lazyValue={resellerDetails}>
                                    {(loadedResellerDetails) => {
                                        const addressMap = buildAddressMap(
                                            loadedResellerDetails.resellerLocations.locations
                                        );
                                        const {
                                            blockers,
                                            flaggedRows,
                                            confirmationNotice,
                                            isScanned,
                                            showsPoNumberConcern,
                                            showsShipDateConcern,
                                            showsDeliveryDateConcern,
                                            shipDateQuotedValue,
                                            deliveryDateQuotedValue,
                                        } = poReviewSubmitState(loadedDraft, Object.keys(addressMap), {
                                            poNumberFindings,
                                            shipDateFindings,
                                            deliveryDateFindings,
                                            tableFindings,
                                        });
                                        const showsBlockedAlert = submitAttempted && blockers.length > 0;
                                        const openModalOrSubmit = () => {
                                            if (
                                                flaggedRows.length === 0 &&
                                                confirmationNotice === null &&
                                                !showsPoNumberConcern &&
                                                !showsShipDateConcern &&
                                                !showsDeliveryDateConcern &&
                                                !isScanned
                                            ) {
                                                submitPurchaseOrder(loadedDraft);
                                            } else {
                                                setIsSubmitModalOpen(true);
                                            }
                                        };
                                        const attemptSubmit = () => {
                                            if (blockers.length === 0) {
                                                openModalOrSubmit();
                                                return;
                                            }
                                            const [firstBlocker] = blockers;
                                            setSubmitAttempted(true);
                                            setTableResetCount((count) => count + 1);
                                            requestAnimationFrame(() => focusControl(blockerTestId(firstBlocker)));
                                        };
                                        return (
                                            <MainColumn width={'100%'}>
                                                <Column spacing={'medium'} width={'100%'} data-testid={TEST_ID}>
                                                    {hasPriceList(resellerPriceList.value) ? (
                                                        <Alert type={'informational'} size={'small'}>
                                                            {t('reviewIntro-alert')}
                                                        </Alert>
                                                    ) : (
                                                        <Alert type={'error'} size={'small'}>
                                                            {t('noPriceList-alert')}
                                                        </Alert>
                                                    )}

                                                    <PoReviewDetailsBox
                                                        t={t}
                                                        onDownloadSourceDocument={handleDownloadSourceDocument}
                                                        fileName={loadedDraft.fileName}
                                                        createdAt={loadedDraft.createdAt}
                                                        source={loadedDraft.source}
                                                        shipToAddress={loadedDraft.shipToAddress?.fullAddress?.value}
                                                        shipToAddressFindings={
                                                            loadedDraft.shipToAddress?.findings ?? []
                                                        }
                                                        poNumber={loadedDraft.poNumber}
                                                        poNumberInError={isPoNumberInError(loadedDraft)}
                                                        poNumberFindings={poNumberFindings}
                                                        onPoNumberChange={setPoNumber}
                                                        poNumberNotRead={loadedDraft.poNumberNotRead}
                                                        poNumberRawValue={loadedDraft.poNumberRawValue}
                                                        shipDate={loadedDraft.shipDate}
                                                        shipDateFindings={shipDateFindings}
                                                        shipDateNotRead={loadedDraft.shipDateNotRead}
                                                        shipDateRawValue={loadedDraft.shipDateRawValue}
                                                        shipDateInPast={loadedDraft.shipDateInPast}
                                                        shipDateQuotedValue={shipDateQuotedValue}
                                                        shipDateConcernActive={showsShipDateConcern}
                                                        onShipDateChange={setShipDate}
                                                        deliveryDate={loadedDraft.deliveryDate}
                                                        deliveryDateExtracted={loadedDraft.deliveryDateExtracted}
                                                        deliveryDateFindings={deliveryDateFindings}
                                                        deliveryDateRawValue={loadedDraft.deliveryDateRawValue}
                                                        deliveryDateInPast={loadedDraft.deliveryDateInPast}
                                                        deliveryDateQuotedValue={deliveryDateQuotedValue}
                                                        deliveryDateConcernActive={showsDeliveryDateConcern}
                                                        onDeliveryDateChange={setDeliveryDate}
                                                        shippingLocationCode={loadedDraft.shippingLocationCode}
                                                        onShippingLocationChange={setShippingLocationCode}
                                                        addressMap={addressMap}
                                                        onRequestTicket={setTicketRequestType}
                                                        dataTestId={`${TEST_ID}.details`}
                                                    />

                                                    <PoReviewLineItemsTile
                                                        t={t}
                                                        rows={loadedDraft.rows}
                                                        tableFindings={tableFindings}
                                                        isScanned={isScanned}
                                                        submitAttempted={submitAttempted}
                                                        tableResetCount={tableResetCount}
                                                        itemMap={itemMap}
                                                        priceList={resellerPriceList.value}
                                                        asinToImageUrls={asinToImageUrls}
                                                        onItemChange={handleItemChange}
                                                        onQuantityChange={setRowQuantity}
                                                        onAddLine={handleAddLine}
                                                        onRemoveLine={removeLine}
                                                        onRequestTicket={setTicketRequestType}
                                                        dataTestId={`${TEST_ID}.lineItems`}
                                                    />

                                                    <Column
                                                        alignmentHorizontal={'right'}
                                                        spacing={'small'}
                                                        width={'100%'}
                                                    >
                                                        {showsBlockedAlert && (
                                                            <Alert type={'error'} size={'small'}>
                                                                {t('submitBlocked-alert')}
                                                            </Alert>
                                                        )}

                                                        <PoReviewFooterActions
                                                            t={t}
                                                            isSubmitDisabled={isSubmitted}
                                                            isSubmitBusy={isPageBusy}
                                                            onCancel={cancel}
                                                            onSubmit={attemptSubmit}
                                                            dataTestIdPrefix={TEST_ID}
                                                        />
                                                    </Column>

                                                    <PoReviewSubmitModal
                                                        open={isSubmitModalOpen}
                                                        flaggedRows={flaggedRows}
                                                        unconfirmedPoNumber={
                                                            showsPoNumberConcern ? loadedDraft.poNumber : undefined
                                                        }
                                                        unconfirmedShipDate={
                                                            showsShipDateConcern ? shipDateQuotedValue : undefined
                                                        }
                                                        unconfirmedDeliveryDate={
                                                            showsDeliveryDateConcern
                                                                ? deliveryDateQuotedValue
                                                                : undefined
                                                        }
                                                        tableNotice={confirmationNotice}
                                                        isScanned={isScanned}
                                                        lineCount={loadedDraft.rows.length}
                                                        formattedSubtotal={formattedEstimatedSubtotal(
                                                            loadedDraft.rows,
                                                            resellerPriceList.value
                                                        )}
                                                        itemMap={itemMap}
                                                        onCancel={() => setIsSubmitModalOpen(false)}
                                                        onSubmit={() => {
                                                            setIsSubmitModalOpen(false);
                                                            submitPurchaseOrder(loadedDraft);
                                                        }}
                                                        t={t}
                                                        dataTestId={`${TEST_ID}.submitModal`}
                                                    />

                                                    {ticketRequestType && (
                                                        <CreateIssueModal
                                                            header={t('raiseTicket-modalHeader')}
                                                            username={username}
                                                            resellerId={resellerId}
                                                            requestType={ticketRequestType}
                                                            showTypePicker={true}
                                                            draftContext={{ extractionId: loadedDraft.extractionId }}
                                                            draftPrefill={{
                                                                poNumber:
                                                                    loadedDraft.poNumber ||
                                                                    loadedDraft.poNumberExtractedValue,
                                                                shipToAddress:
                                                                    loadedDraft.shipToAddress?.fullAddress?.value,
                                                            }}
                                                            initialOpen={true}
                                                            onClose={() => setTicketRequestType(undefined)}
                                                            dataTestId={`${TEST_ID}.ticketModal`}
                                                        />
                                                    )}
                                                </Column>
                                            </MainColumn>
                                        );
                                    }}
                                </WaitFor>
                            )}
                        </WaitFor>
                    )}
                </WaitFor>
            )}
        </WaitFor>
    );

    return pageBody;
};

const mapStateToProps = ({ poReviewDraftReducer, resellerReducer, userReducer }: RootState) => ({
    asinToImageUrls: resellerReducer.asinToImageUrls,
    draft: poReviewDraftReducer.draft,
    resellerId: resellerReducer.selectedReseller.value!.resellerId,
    username: userReducer.user?.username ?? '',
    resellerDetails: resellerReducer.resellerDetails,
    resellerItems: resellerReducer.resellerItems,
    resellerPriceList: resellerReducer.resellerPriceList,
    unmappedItemStatus: resellerReducer.unmappedItemStatus,
});

const mapDispatchToProps = {
    clearPoReviewDraft,
    getResellerDetails,
    getResellerItems,
    getResellerPriceList,
    getUnmappedItems,
    loadPoReviewDraft,
    addLine,
    removeLine,
    setDeliveryDate,
    setPoNumber,
    setRowItem,
    setRowQuantity,
    setShipDate,
    setShippingLocationCode,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
export type PurchaseOrderReviewPageProps = ConnectedProps<typeof connector>;
export default connector(PurchaseOrderReviewPage);
